import { useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useToast } from "~/hooks/use-toast";
import logger from "~/utils/logger";
import { useTextSnipMutation } from "./useTextSnipMutation";

type NormalizedRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type StoredBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type TextSnipState =
  | { status: "idle" }
  | {
      status: "drawing";
      startX: number;
      startY: number;
      currentX: number;
      currentY: number;
    }
  | { status: "processing"; rect: NormalizedRect }
  | { status: "copying"; rect: NormalizedRect; text: string }
  | { status: "success"; text: string }
  | { status: "error"; rect: NormalizedRect; message: string }
  | { status: "empty"; rect: NormalizedRect };

const MIN_RECT_SIZE = 20;
const SUCCESS_FADE_DELAY_MS = 300;
const PREVIEW_MAX_LENGTH = 80;

type SnipCompositionParams = {
  pageId: string;
  organizationId: string;
  projectId: string;
  rotation: number;
};

type UseTextSnipReturn = {
  snipState: TextSnipState;
  isSnipActive: boolean;
  startSnip: () => void;
  cancelSnip: () => void;
  handlers: {
    onMouseDown: (
      e: React.MouseEvent,
      containerRect: DOMRect,
      imgRect: DOMRect
    ) => void;
    onMouseMove: (e: React.MouseEvent, containerRect: DOMRect) => void;
    onMouseUp: () => void;
  };
};

function normalizeRect(
  startX: number,
  startY: number,
  currentX: number,
  currentY: number
): NormalizedRect {
  const x = Math.min(startX, currentX);
  const y = Math.min(startY, currentY);
  const width = Math.abs(currentX - startX);
  const height = Math.abs(currentY - startY);
  return { x, y, width, height };
}

function containerToImageRect(
  rect: NormalizedRect,
  containerBounds: StoredBounds | null,
  imageBounds: StoredBounds | null
): NormalizedRect {
  if (
    !containerBounds ||
    !imageBounds ||
    imageBounds.width === 0 ||
    imageBounds.height === 0
  ) {
    return rect;
  }

  const imgOffsetX = imageBounds.left - containerBounds.left;
  const imgOffsetY = imageBounds.top - containerBounds.top;

  const pixelX = rect.x * containerBounds.width;
  const pixelY = rect.y * containerBounds.height;
  const pixelW = rect.width * containerBounds.width;
  const pixelH = rect.height * containerBounds.height;

  const imgX = (pixelX - imgOffsetX) / imageBounds.width;
  const imgY = (pixelY - imgOffsetY) / imageBounds.height;
  const imgW = pixelW / imageBounds.width;
  const imgH = pixelH / imageBounds.height;

  const clampedX = Math.max(0, Math.min(1, imgX));
  const clampedY = Math.max(0, Math.min(1, imgY));

  return {
    x: clampedX,
    y: clampedY,
    width: Math.max(0, Math.min(1, imgX + imgW) - clampedX),
    height: Math.max(0, Math.min(1, imgY + imgH) - clampedY),
  };
}

function inverseRotateRect(
  rect: NormalizedRect,
  rotation: number
): NormalizedRect {
  const normalized = ((rotation % 360) + 360) % 360;
  switch (normalized) {
    case 90:
      return {
        x: rect.y,
        y: 1 - rect.x - rect.width,
        width: rect.height,
        height: rect.width,
      };
    case 180:
      return {
        x: 1 - rect.x - rect.width,
        y: 1 - rect.y - rect.height,
        width: rect.width,
        height: rect.height,
      };
    case 270:
      return {
        x: 1 - rect.y - rect.height,
        y: rect.x,
        width: rect.height,
        height: rect.width,
      };
    default:
      return rect;
  }
}

export function useTextSnip(
  params: SnipCompositionParams | null
): UseTextSnipReturn {
  const [snipState, setSnipState] = useState<TextSnipState>({
    status: "idle",
  });
  const snipStateRef = useRef<TextSnipState>(snipState);
  const { toast } = useToast();
  const intl = useIntl();
  const mutation = useTextSnipMutation();
  const containerSizeRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const containerBoundsRef = useRef<StoredBounds | null>(null);
  const imageBoundsRef = useRef<StoredBounds | null>(null);

  const isSnipActive = snipState.status !== "idle";

  const startSnip = useCallback(() => {
    snipStateRef.current = { status: "idle" };
    setSnipState({ status: "idle" });
  }, []);

  const cancelSnip = useCallback(() => {
    mutation.reset();
    snipStateRef.current = { status: "idle" };
    setSnipState({ status: "idle" });
  }, [mutation]);

  const processSnip = useCallback(
    async (rect: NormalizedRect) => {
      if (!params) return;

      setSnipState({ status: "processing", rect });

      try {
        const adjustedRect = containerToImageRect(
          rect,
          containerBoundsRef.current,
          imageBoundsRef.current
        );

        const imageRect = inverseRotateRect(adjustedRect, params.rotation);

        const result = await mutation.mutateAsync({
          organizationId: params.organizationId,
          projectId: params.projectId,
          pageId: params.pageId,
          rect: imageRect,
        });

        const extractedText = result.text.trim();

        if (!extractedText) {
          setSnipState({ status: "empty", rect });
          toast({
            title: intl.formatMessage({
              id: "docViewer.textSnip.empty.title",
            }),
            description: intl.formatMessage({
              id: "docViewer.textSnip.empty.description",
            }),
          });
          setTimeout(() => setSnipState({ status: "idle" }), 2000);
          return;
        }

        setSnipState({ status: "copying", rect, text: extractedText });

        try {
          await navigator.clipboard.writeText(extractedText);
        } catch {
          const textarea = document.createElement("textarea");
          textarea.value = extractedText;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }

        setSnipState({ status: "success", text: extractedText });

        const preview =
          extractedText.length > PREVIEW_MAX_LENGTH
            ? extractedText.substring(0, PREVIEW_MAX_LENGTH) + "..."
            : extractedText;

        toast({
          title: intl.formatMessage({
            id: "docViewer.textSnip.success.title",
          }),
          description: intl.formatMessage(
            { id: "docViewer.textSnip.success.description" },
            { preview }
          ),
        });

        setTimeout(
          () => setSnipState({ status: "idle" }),
          SUCCESS_FADE_DELAY_MS
        );
      } catch (error) {
        logger.error("Text snip failed", { error });
        setSnipState({
          status: "error",
          rect,
          message:
            error instanceof Error ? error.message : "Unknown error occurred",
        });
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "docViewer.textSnip.error.title",
          }),
          description: intl.formatMessage({
            id: "docViewer.textSnip.error.description",
          }),
        });
        setTimeout(() => setSnipState({ status: "idle" }), 3000);
      }
    },
    [params, mutation, toast, intl]
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent, containerRect: DOMRect, imgRect: DOMRect) => {
      if (
        snipState.status !== "idle" &&
        snipState.status !== "drawing" &&
        snipState.status !== "error" &&
        snipState.status !== "empty"
      ) {
        return;
      }

      containerSizeRef.current = {
        width: containerRect.width,
        height: containerRect.height,
      };

      containerBoundsRef.current = {
        left: containerRect.left,
        top: containerRect.top,
        width: containerRect.width,
        height: containerRect.height,
      };

      imageBoundsRef.current = {
        left: imgRect.left,
        top: imgRect.top,
        width: imgRect.width,
        height: imgRect.height,
      };

      const startX = (e.clientX - containerRect.left) / containerRect.width;
      const startY = (e.clientY - containerRect.top) / containerRect.height;

      const newState: TextSnipState = {
        status: "drawing",
        startX: Math.max(0, Math.min(1, startX)),
        startY: Math.max(0, Math.min(1, startY)),
        currentX: Math.max(0, Math.min(1, startX)),
        currentY: Math.max(0, Math.min(1, startY)),
      };
      snipStateRef.current = newState;
      setSnipState(newState);
    },
    [snipState.status]
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent, containerRect: DOMRect) => {
      if (snipStateRef.current.status !== "drawing") return;

      const currentX = (e.clientX - containerRect.left) / containerRect.width;
      const currentY = (e.clientY - containerRect.top) / containerRect.height;

      setSnipState((prev) => {
        if (prev.status !== "drawing") return prev;
        const updated: TextSnipState = {
          ...prev,
          currentX: Math.max(0, Math.min(1, currentX)),
          currentY: Math.max(0, Math.min(1, currentY)),
        };
        snipStateRef.current = updated;
        return updated;
      });
    },
    []
  );

  const onMouseUp = useCallback(() => {
    const current = snipStateRef.current;
    if (current.status !== "drawing") return;

    const rect = normalizeRect(
      current.startX,
      current.startY,
      current.currentX,
      current.currentY
    );

    const { width: containerWidth, height: containerHeight } =
      containerSizeRef.current;
    const rectWidthPx = rect.width * containerWidth;
    const rectHeightPx = rect.height * containerHeight;

    if (rectWidthPx < MIN_RECT_SIZE || rectHeightPx < MIN_RECT_SIZE) {
      setSnipState({ status: "idle" });
      return;
    }

    processSnip(rect);
  }, [processSnip]);

  return {
    snipState,
    isSnipActive,
    startSnip,
    cancelSnip,
    handlers: {
      onMouseDown,
      onMouseMove,
      onMouseUp,
    },
  };
}

export type { NormalizedRect, TextSnipState };
